import { SessionWithData } from '@zond/api-interfaces';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface SessionsTableProps {
  sessions: SessionWithData[];
  loading: boolean;
}

export const SessionsTable: React.FC<SessionsTableProps> = ({ sessions, loading }) => {
  const { t } = useTranslation('reports');
  const processedSessions = useMemo(() => {
    return (sessions || [])
      .map((session) => {
        const start = new Date(session.createdAt);
        const end = new Date(session.EndSession?.[0]?.createdAt ?? session.createdAt);
        const duration = end.getTime() - start.getTime();
        return {
          ...session,
          duration,
          start: format(start, 'MMM d, h:mm a'),
        };
      })
      .filter((session) => session.duration > 0);
  }, [sessions]);

  // Function for formatting duration (equivalent to humanizeTime pipe)
  const humanizeTime = (milliseconds: number): string => {
    const seconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;

    if (hours > 0) {
      return t('time.hoursMinutes', { hours, minutes });
    } else if (minutes > 0) {
      return t('time.minutesSeconds', { minutes, seconds: remainingSeconds });
    } else {
      return t('time.seconds', { seconds: remainingSeconds });
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border shadow-sm">
        <div className="flex flex-col space-y-1.5 p-6">
          <h3 className="text-2xl font-semibold leading-none tracking-tight">{t('sessions.title')}</h3>
        </div>
        <div className="p-6 pt-0 grid gap-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border shadow-sm">
      <div className="flex flex-col space-y-1.5 p-6">
        <h3 className="text-2xl font-semibold leading-none tracking-tight">{t('sessions.title')}</h3>
      </div>
      <div className="p-6 pt-0 grid gap-8 max-h-[500px] overflow-auto custom-scrollbar">
        {processedSessions.length > 0 ? (
          <table className="w-full caption-bottom text-sm">
            <thead className="border-b sticky top-0 bg-card">
              <tr className="border-b transition-colors">
                <th className="h-12 px-4 text-left align-middle font-medium text-gray-500">
                  {t('sessions.columnStartTime')}
                </th>
                <th className="h-12 px-4 align-middle font-medium text-gray-500 text-right">
                  {t('sessions.columnDuration')}
                </th>
              </tr>
            </thead>
            <tbody>
              {processedSessions.map((session, index) => (
                <tr key={session.id || index} className="border-b transition-colors hover:bg-muted/50">
                  <td className="p-4 align-middle flex items-center">
                    <div className="font-medium">{session.start}</div>
                  </td>
                  <td className="text-right p-4">{humanizeTime(session.duration)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="h-12 flex items-center justify-center border rounded-lg p-2 mb-4">
              <Clock className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-xl font-semibold mb-2">{t('sessions.emptyTitle')}</h3>
            <p className="text-muted-foreground">{t('sessions.emptyDescription')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
